import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  Button,
  Divider,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  PaymentRequisitionWithRelations,
  useGetPaymentRequisitionsQuery,
} from '../../../redux/vmsApi';
import { getCurrentDate } from '../../../utils/dateUtil';

type StageKey =
  | 'created'
  | 'reviewed'
  | 'prioritized'
  | 'scheduled'
  | 'confirmed'
  | 'all';

type DimensionKey =
  | 'actor'
  | 'department'
  | 'development'
  | 'project'
  | 'batchNumber'
  | 'paymentMethod'
  | 'currency';

interface StageMetric {
  count: number;
  value: number;
}

interface StageLeader {
  name: string;
  count: number;
  value: number;
}

const STAGE_COLORS: Record<StageKey, string> = {
  created: '#2E7D32',
  reviewed: '#1976D2',
  prioritized: '#EF6C00',
  scheduled: '#8E24AA',
  confirmed: '#283593',
  all: '#455A64',
};

const STAGE_LABELS: Record<StageKey, string> = {
  created: 'Created / Prepared',
  reviewed: 'Reviewed',
  prioritized: 'Prioritized',
  scheduled: 'Scheduled',
  confirmed: 'Confirmed / Paid',
  all: 'All Payment Requests',
};

const STAGES_WITH_CHARTS: StageKey[] = [
  'created',
  'reviewed',
  'prioritized',
  'scheduled',
  'confirmed',
];

const getUserName = (user: unknown): string => {
  if (!user || typeof user !== 'object') {
    return 'Unassigned';
  }

  const candidate = user as { name?: string; email?: string };
  return candidate.name || candidate.email || 'Unassigned';
};

const getStageDate = (req: PaymentRequisitionWithRelations, stage: StageKey): string | undefined => {
  if (stage === 'created' || stage === 'all') {
    return req.dateCreated;
  }

  if (stage === 'reviewed') {
    return req.reviewedAt;
  }

  if (stage === 'scheduled') {
    return req.scheduledAt || req.scheduledOn || req.paymentDate || req.lastActionedDate;
  }

  if (stage === 'prioritized') {
    return req.prioritizedAt || req.scheduledAt || req.scheduledOn || req.paymentDate || req.lastActionedDate;
  }

  return req.paymentConfirmedAt || req.paymentDate;
};

const parseDateValue = (value: unknown): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value === 'object') {
    const maybeDate = (value as { $date?: string }).$date;
    if (maybeDate) {
      const parsed = new Date(maybeDate);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
  }

  return null;
};

const isInRange = (rawDate: unknown, fromDate: string, endDate: string): boolean => {
  const date = parseDateValue(rawDate);
  if (!date) {
    return false;
  }

  const from = new Date(`${fromDate}T00:00:00`);
  const to = new Date(`${endDate}T23:59:59.999`);

  return date >= from && date <= to;
};

const getDimensionValue = (
  req: PaymentRequisitionWithRelations,
  stage: StageKey,
  dimension: DimensionKey
): string => {
  if (dimension === 'actor') {
    if (stage === 'created' || stage === 'all') {
      return getUserName(req.preparedBy);
    }
    if (stage === 'reviewed') {
      return getUserName(req.reviewedBy);
    }
    if (stage === 'scheduled') {
      return getUserName(req.scheduledBy);
    }
    if (stage === 'prioritized') {
      return getUserName(req.prioritizedBy);
    }

    return getUserName(req.paymentConfirmedBy);
  }

  const value = req[dimension];
  if (value === null || value === undefined || value === '') {
    return 'Unspecified';
  }

  return String(value);
};

const formatAmount = (value: number) =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);

const PaymentRequestsDashboard: React.FC = () => {
  const currentDate = getCurrentDate();

  const [filters, setFilters] = useState({
    fromDate: '2025-01-16',
    toDate: currentDate,
    development: '',
    project: '',
    department: '',
    paymentMethod: '',
    currency: '',
  });

  const [apiParams, setApiParams] = useState({
    skip: 0,
    limit: 200000,
    development: '',
    project: '',
    department: '',
    paymentMethod: '',
    currency: '',
  });

  const [dimensionByStage, setDimensionByStage] = useState<Record<StageKey, DimensionKey>>({
    created: 'actor',
    reviewed: 'actor',
    prioritized: 'actor',
    scheduled: 'actor',
    confirmed: 'actor',
    all: 'actor',
  });

  const { data: paymentRequests, isLoading, isError } = useGetPaymentRequisitionsQuery(apiParams);

  const requests = paymentRequests ?? [];

  const options = useMemo(() => {
    const unique = {
      developments: new Set<string>(),
      projects: new Set<string>(),
      departments: new Set<string>(),
      paymentMethods: new Set<string>(),
      currencies: new Set<string>(),
    };

    requests.forEach((req) => {
      if (req.development) unique.developments.add(req.development);
      if (req.project) unique.projects.add(req.project);
      if (req.department) unique.departments.add(req.department);
      if (req.paymentMethod) unique.paymentMethods.add(req.paymentMethod);
      if (req.currency) unique.currencies.add(req.currency);
    });

    return {
      developments: Array.from(unique.developments).sort(),
      projects: Array.from(unique.projects).sort(),
      departments: Array.from(unique.departments).sort(),
      paymentMethods: Array.from(unique.paymentMethods).sort(),
      currencies: Array.from(unique.currencies).sort(),
    };
  }, [requests]);

  const stageRequests = useMemo(() => {
    const map: Record<StageKey, PaymentRequisitionWithRelations[]> = {
      created: [],
      reviewed: [],
      prioritized: [],
      scheduled: [],
      confirmed: [],
      all: [],
    };

    const stageKeys = Object.keys(map) as StageKey[];

    stageKeys.forEach((stage) => {
      map[stage] = requests.filter((req) => {
        const isWithinRange = isInRange(getStageDate(req, stage), filters.fromDate, filters.toDate);

        if (stage === 'scheduled') {
          const isScheduledOrPaid = req.status === 'Scheduled' || req.status === 'Paid';
          return isWithinRange && isScheduledOrPaid;
        }

        if (stage === 'prioritized') {
          const isPrioritizedOrBeyond =
            req.status === 'Prioritized' || req.status === 'Scheduled' || req.status === 'Paid';
          return isWithinRange && isPrioritizedOrBeyond;
        }

        if (stage === 'confirmed') {
          return isWithinRange && req.status === 'Paid';
        }

        return isWithinRange;
      });
    });

    return map;
  }, [requests, filters.fromDate, filters.toDate]);

  const metrics = useMemo(() => {
    const keys = Object.keys(stageRequests) as StageKey[];
    const output = {} as Record<StageKey, StageMetric>;

    keys.forEach((stage) => {
      const rows = stageRequests[stage];
      output[stage] = {
        count: rows.length,
        value: rows.reduce((sum, req) => sum + Number(req.amount || 0), 0),
      };
    });

    return output;
  }, [stageRequests]);

  const pendingMetrics = useMemo(
    () => ({
      notReviewed: Math.max(metrics.created.count - metrics.reviewed.count, 0),
      notPrioritized: Math.max(metrics.reviewed.count - metrics.prioritized.count, 0),
      unscheduled: Math.max(metrics.prioritized.count - metrics.scheduled.count, 0),
      unconfirmed: Math.max(metrics.scheduled.count - metrics.confirmed.count, 0),
    }),
    [metrics]
  );

  const topActorByStage = useMemo(() => {
    const stageLeaders = {} as Record<
      StageKey,
      { byCount: StageLeader | null; byValue: StageLeader | null }
    >;

    (Object.keys(stageRequests) as StageKey[]).forEach((stage) => {
      const grouped: Record<string, { count: number; value: number }> = {};

      stageRequests[stage].forEach((req) => {
        const actor = getDimensionValue(req, stage, 'actor');
        if (!grouped[actor]) {
          grouped[actor] = { count: 0, value: 0 };
        }

        grouped[actor].count += 1;
        grouped[actor].value += Number(req.amount || 0);
      });

      const leaders = Object.entries(grouped).map(([name, stats]) => ({
        name,
        count: stats.count,
        value: stats.value,
      }));

      const byCount =
        leaders.length > 0
          ? leaders.reduce((best, current) => {
              if (current.count > best.count) {
                return current;
              }

              if (current.count === best.count && current.value > best.value) {
                return current;
              }

              return best;
            }, leaders[0])
          : null;

      const byValue =
        leaders.length > 0
          ? leaders.reduce((best, current) => {
              if (current.value > best.value) {
                return current;
              }

              if (current.value === best.value && current.count > best.count) {
                return current;
              }

              return best;
            }, leaders[0])
          : null;

      stageLeaders[stage] = { byCount, byValue };
    });

    return stageLeaders;
  }, [stageRequests]);

  const getDistributionData = (stage: StageKey) => {
    const dimension = dimensionByStage[stage];
    const grouped: Record<string, { count: number; value: number }> = {};

    stageRequests[stage].forEach((req) => {
      const key = getDimensionValue(req, stage, dimension);
      if (!grouped[key]) {
        grouped[key] = { count: 0, value: 0 };
      }
      grouped[key].count += 1;
      grouped[key].value += Number(req.amount || 0);
    });

    return Object.entries(grouped)
      .map(([name, values]) => ({ name, ...values }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  };

  const getTrendData = (stage: StageKey) => {
    const grouped: Record<string, { count: number; value: number }> = {};

    stageRequests[stage].forEach((req) => {
      const date = parseDateValue(getStageDate(req, stage));
      if (!date) {
        return;
      }

      const dayKey = date.toISOString().slice(0, 10);
      if (!grouped[dayKey]) {
        grouped[dayKey] = { count: 0, value: 0 };
      }

      grouped[dayKey].count += 1;
      grouped[dayKey].value += Number(req.amount || 0);
    });

    return Object.entries(grouped)
      .map(([day, values]) => ({ day, ...values }))
      .sort((a, b) => (a.day > b.day ? 1 : -1));
  };

  const handleFilterChange = (field: keyof typeof filters, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setApiParams((prev) => ({
      ...prev,
      development: filters.development,
      project: filters.project,
      department: filters.department,
      paymentMethod: filters.paymentMethod,
      currency: filters.currency,
    }));
  };

  if (isLoading) {
    return <Typography>Loading payment dashboard...</Typography>;
  }

  if (isError) {
    return <Typography>Error loading payment dashboard data.</Typography>;
  }

  return (
    <Stack spacing={3}>
      <Paper
        sx={{
          p: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #E3F2FD 0%, #E8F5E9 100%)',
          color: '#0F2940',
          boxShadow: 4,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Payment Requests Analytics
        </Typography>
        <Typography variant="body1" sx={{ color: '#264653' }}>
          Live operational intelligence for prepared, reviewed, approved, prioritized, scheduled, and paid requests.
        </Typography>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={2.4}>
            <TextField
              label="From Date"
              type="date"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ bgcolor: '#fff', borderRadius: 1 }}
            />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <TextField
              label="To Date"
              type="date"
              value={filters.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ bgcolor: '#fff', borderRadius: 1 }}
            />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <FormControl fullWidth sx={{ bgcolor: '#fff', borderRadius: 1 }}>
              <InputLabel>Department</InputLabel>
              <Select
                value={filters.department}
                label="Department"
                onChange={(e) => handleFilterChange('department', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {options.departments.map((department) => (
                  <MenuItem key={department} value={department}>
                    {department}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <FormControl fullWidth sx={{ bgcolor: '#fff', borderRadius: 1 }}>
              <InputLabel>Development</InputLabel>
              <Select
                value={filters.development}
                label="Development"
                onChange={(e) => handleFilterChange('development', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {options.developments.map((development) => (
                  <MenuItem key={development} value={development}>
                    {development}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <Button
              fullWidth
              variant="contained"
              onClick={applyFilters}
              sx={{
                height: '100%',
                minHeight: 56,
                backgroundColor: '#0D47A1',
                fontWeight: 700,
                '&:hover': { backgroundColor: '#08306B' },
              }}
            >
              Apply Filters
            </Button>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth sx={{ bgcolor: '#fff', borderRadius: 1 }}>
              <InputLabel>Project</InputLabel>
              <Select
                value={filters.project}
                label="Project"
                onChange={(e) => handleFilterChange('project', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {options.projects.map((project) => (
                  <MenuItem key={project} value={project}>
                    {project}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth sx={{ bgcolor: '#fff', borderRadius: 1 }}>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={filters.paymentMethod}
                label="Payment Method"
                onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {options.paymentMethods.map((paymentMethod) => (
                  <MenuItem key={paymentMethod} value={paymentMethod}>
                    {paymentMethod}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth sx={{ bgcolor: '#fff', borderRadius: 1 }}>
              <InputLabel>Currency</InputLabel>
              <Select
                value={filters.currency}
                label="Currency"
                onChange={(e) => handleFilterChange('currency', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {options.currencies.map((currency) => (
                  <MenuItem key={currency} value={currency}>
                    {currency.toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        {(Object.keys(STAGE_LABELS) as StageKey[]).map((stage) => (
          <Grid key={stage} item xs={12} md={6} lg={4}>
            <Card sx={{ borderTop: `5px solid ${STAGE_COLORS[stage]}`, borderRadius: 2, boxShadow: 3 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#607D8B', fontWeight: 700 }}>
                      {STAGE_LABELS[stage]}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: STAGE_COLORS[stage], my: 1 }}>
                      {metrics[stage].count}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#37474F' }}>
                      Total Value: {formatAmount(metrics[stage].value)}
                    </Typography>
                  </Box>

                  <Box sx={{ minWidth: 170, textAlign: 'right' }}>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" sx={{ mb: 0.5 }}>
                      <EmojiEventsIcon sx={{ color: '#F9A825', fontSize: 20 }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#5D4037' }}>
                        Top Performer
                      </Typography>
                    </Stack>

                    {topActorByStage[stage]?.byCount ? (
                      <Typography variant="caption" sx={{ display: 'block', color: '#455A64' }}>
                        Count: {topActorByStage[stage].byCount?.name} ({topActorByStage[stage].byCount?.count})
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ display: 'block', color: '#90A4AE' }}>
                        Count: N/A
                      </Typography>
                    )}

                    {topActorByStage[stage]?.byValue ? (
                      <Typography variant="caption" sx={{ display: 'block', color: '#455A64' }}>
                        Value: {topActorByStage[stage].byValue?.name} ({formatAmount(topActorByStage[stage].byValue?.value || 0)})
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ display: 'block', color: '#90A4AE' }}>
                        Value: N/A
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 2.5, borderRadius: 2.5, boxShadow: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
          Workflow Gaps (Same Date Range)
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} flexWrap="wrap">
          <Chip color="warning" label={`Not Reviewed: ${pendingMetrics.notReviewed}`} />
          <Chip color="warning" label={`Not Prioritized: ${pendingMetrics.notPrioritized}`} />
          <Chip color="warning" label={`Unscheduled: ${pendingMetrics.unscheduled}`} />
          <Chip color="warning" label={`Unconfirmed: ${pendingMetrics.unconfirmed}`} />
        </Stack>
      </Paper>

      {STAGES_WITH_CHARTS.map((stage) => {
        const trendData = getTrendData(stage);
        const distributionData = getDistributionData(stage);

        return (
          <Paper key={stage} sx={{ p: 2.5, borderRadius: 2.5, boxShadow: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: STAGE_COLORS[stage] }}>
                {STAGE_LABELS[stage]} Analytics
              </Typography>

              <FormControl size="small" sx={{ minWidth: 280 }}>
                <InputLabel>Breakdown By</InputLabel>
                <Select
                  value={dimensionByStage[stage]}
                  label="Breakdown By"
                  onChange={(e) =>
                    setDimensionByStage((prev) => ({
                      ...prev,
                      [stage]: e.target.value as DimensionKey,
                    }))
                  }
                >
                  <MenuItem value="actor">User</MenuItem>
                  <MenuItem value="department">Department</MenuItem>
                  <MenuItem value="development">Development</MenuItem>
                  <MenuItem value="project">Project</MenuItem>
                  <MenuItem value="batchNumber">Batch Number</MenuItem>
                  <MenuItem value="paymentMethod">Payment Method</MenuItem>
                  <MenuItem value="currency">Currency</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} lg={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Period Trend (Count and Value)
                </Typography>
                <Box sx={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="count" stroke={STAGE_COLORS[stage]} strokeWidth={2.5} />
                      <Line yAxisId="right" type="monotone" dataKey="value" stroke="#00897B" strokeWidth={2.5} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>

              <Grid item xs={12} lg={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Top Distribution
                </Typography>
                <Box sx={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <BarChart data={distributionData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" interval={0} angle={-15} textAnchor="end" height={70} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="count" fill={STAGE_COLORS[stage]} name="Count" />
                      <Bar dataKey="value" fill="#26A69A" name="Value" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        );
      })}
    </Stack>
  );
};

export default PaymentRequestsDashboard;
